import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Heart, Brain, Ribbon, Droplet, ForkKnife } from "lucide-react";
import RenalDietTracker from "@/components/diet/RenalDietTracker";
import DietGuidelines from "@/components/DietGuidelines";
import { useTelemetry } from "@/lib/telemetry";

const SPECIALTY_META = {
  nephrology: { icon: Droplet, accent: "#4ADE80", label: "Nephrology" },
  cardiology: { icon: Heart,   accent: "#F87171", label: "Cardiology" },
  neurology:  { icon: Brain,   accent: "#A78BFA", label: "Neurology" },
  oncology:   { icon: Ribbon,  accent: "#FBBF24", label: "Oncology" },
};
const TAB_ORDER = ["nephrology", "cardiology", "neurology", "oncology"];

/**
 * Diet — multi-specialty hub (Phase 3, partial).
 * Nephrology tab hosts the legacy interactive tracker (food logging + rings).
 * Cardio / Neuro / Onco tabs render the static per-specialty DietGuidelines
 * (pattern + targets + eat-more / eat-less + sample day + red flags).
 */
export default function Diet() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get("tab") || user?.specialty || "nephrology";
  const [tab, setTab] = useState(TAB_ORDER.includes(initial) ? initial : "nephrology");

  const setActive = (t) => {
    setTab(t);
    setSearchParams({ tab: t });
  };
  useTelemetry("diet-optimizer", "tab_switch", tab);

  return (
    <div className="space-y-6" data-testid="diet-page">
      <div className="ros-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
               style={{ background: "rgba(74,222,128,0.14)", border: "1px solid rgba(74,222,128,0.4)" }}>
            <ForkKnife size={22} style={{ color: "#4ADE80" }} />
          </div>
          <div>
            <h2 className="font-heading text-2xl">Diet optimizer</h2>
            <p className="text-sm text-[#A3ACA7] mt-0.5">
              Evidence-based nutrition per specialty. Nephrology has an interactive tracker; other departments show a
              guideline reference — targets, food lists, sample day, and safety flags.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="diet-tabs">
        {TAB_ORDER.map((s) => {
          const meta = SPECIALTY_META[s];
          const Icon = meta.icon;
          const active = tab === s;
          return (
            <button
              key={s}
              onClick={() => setActive(s)}
              data-testid={`diet-tab-${s}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                active ? "text-black border-transparent"
                       : "bg-[#0F1312] text-[#A3ACA7] border-[#272E2B] hover:text-white"
              }`}
              style={active ? { background: meta.accent } : {}}
            >
              <Icon size={14} /> {meta.label}
            </button>
          );
        })}
      </div>

      {tab === "nephrology" ? <RenalDietTracker /> : <DietGuidelines specialty={tab} />}
    </div>
  );
}
